import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { t } from '../lib/i18n';
import { codeOf, messageFor, requestIdOf } from '../lib/errors';

/**
 * The parts every screen in the product shares.
 *
 * Kept small on purpose. What a list screen needs is a title, somewhere for its actions, a place for
 * filters, and honest answers for the three states a list is actually in most of the time: loading,
 * empty, and broken. Writing those per screen is how a product ends up with four different empty
 * states and one screen that silently shows nothing when the request fails.
 */

export function PageHeader({
  title,
  description,
  actions,
  visual,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  /** Something to draw beside the title, such as the face of the person this screen is about. */
  visual?: ReactNode;
}): React.JSX.Element {
  return (
    <header className="view__header">
      <div className="view__heading">
        {visual}
        <div>
          <h1>{title}</h1>
          {description && <p className="muted">{description}</p>}
        </div>
      </div>

      {actions && <div className="view__actions">{actions}</div>}
    </header>
  );
}

export function Toolbar({ children }: { children: ReactNode }): React.JSX.Element {
  return <div className="toolbar">{children}</div>;
}

export function Panel({
  title,
  description,
  children,
  actions,
}: {
  title?: string;
  /** One line under the title, for a panel whose purpose is not obvious from its name. */
  description?: string;
  children: ReactNode;
  actions?: ReactNode;
}): React.JSX.Element {
  return (
    <section className="panel">
      {(title || actions) && (
        <div className="panel__header">
          {title && (
            <div className="panel__heading">
              <h2>{title}</h2>
              {description && <p className="muted small">{description}</p>}
            </div>
          )}
          {actions}
        </div>
      )}
      <div className="panel__body">{children}</div>
    </section>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}): React.JSX.Element {
  return (
    <div className="empty">
      <p className="empty__title">{title}</p>
      {description && <p className="muted">{description}</p>}
      {action}
    </div>
  );
}

/**
 * A request that failed.
 *
 * Shows what went wrong and the request id, because that is the one thing support can actually trace,
 * and offers the retry — a failed list is usually a blip, not a dead end.
 */
export function ErrorState({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry?: () => void;
}): React.JSX.Element {
  const requestId = requestIdOf(error);

  // Not a failure at all: the plan does not include this. Say so, and where to change that.
  if (codeOf(error) === 'billing.feature_not_entitled') {
    return (
      <div className="empty empty--locked">
        <p className="empty__title">{t('state.lockedTitle')}</p>
        <p className="muted">{t('state.lockedBody')}</p>
        <Link to="/configuracoes/assinatura" className="button button--compact">
          {t('state.lockedAction')}
        </Link>
      </div>
    );
  }

  return (
    <div className="empty empty--error" role="alert">
      <p className="empty__title">{t('state.errorTitle')}</p>
      <p className="muted">{messageFor(error)}</p>
      {requestId && (
        <p className="muted small">
          {t('state.requestId')}: <code>{requestId}</code>
        </p>
      )}
      {onRetry && (
        <button type="button" className="button button--quiet" onClick={onRetry}>
          {t('state.retry')}
        </button>
      )}
    </div>
  );
}

export function Loading({ label }: { label?: string }): React.JSX.Element {
  return (
    <div className="empty" aria-busy="true">
      <p className="muted">{label ?? t('state.loading')}</p>
    </div>
  );
}

export type BadgeTone = 'neutral' | 'positive' | 'warning' | 'danger' | 'info';

export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: BadgeTone;
}): React.JSX.Element {
  return <span className={`badge badge--${tone}`}>{children}</span>;
}

/**
 * A screen that has an API but no interface yet.
 *
 * It exists so the rail can be honest. A menu entry that opens a blank page, or worse a page of
 * plausible-looking sample data, is how a product lies to the person evaluating it
 * (context.md section 194).
 */
export function NotBuiltYet({
  area,
  endpoints,
}: {
  area: string;
  endpoints?: string[];
}): React.JSX.Element {
  return (
    <div className="view">
      <PageHeader title={area} description={t('state.notBuiltDescription')} />

      {endpoints && endpoints.length > 0 && (
        <Panel title={t('state.availableApi')}>
          <ul className="endpoint-list">
            {endpoints.map((endpoint) => (
              <li key={endpoint}>
                <code>{endpoint}</code>
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </div>
  );
}
